import { useEffect, MouseEvent as ReactMouseEvent } from "react";
import { useNotifications } from "./useNotifications";
import { ErrorToast, InfoToast, SuccessToast, WarningToast } from "./Toast";
import { actionIsHandler } from "./Toast/actionGuards";
import { Toast } from "./types";

const toastNeedsCloseButton = (toast: Toast) => {
  return Boolean(toast.shouldCloseManually || toast.onClose);
};

type useSelfClosingToastArgs = {
  toast: Toast;
  removeToasts: ReturnType<typeof useNotifications>["removeToasts"];
  timeOut?: number;
};

const useSelfClosingToast = ({
  toast,
  removeToasts,
  timeOut = 3000,
}: useSelfClosingToastArgs) => {
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (toastNeedsCloseButton(toast)) {
      return;
    }
    const timeout = setTimeout(() => {
      removeToasts([toast]);
    }, timeOut);
    return () => {
      clearTimeout(timeout);
    };
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */
};

type ToastContainerProps = {
  toast: Toast;
};

const ToastContainer = ({ toast }: ToastContainerProps) => {
  const {
    type,
    title,
    action,
    className,
    timeOut,
    primaryButton,
    secondaryButton,
  } = toast;
  const { removeToasts } = useNotifications();
  useSelfClosingToast({ toast, removeToasts, timeOut });

  const onClose = toastNeedsCloseButton(toast)
    ? {
        onClose: (e: ReactMouseEvent<HTMLButtonElement>) => {
          if (toast.onClose) {
            toast.onClose(e);
          }
          removeToasts([toast]);
        },
      }
    : {};
  const mappedAction =
    action && actionIsHandler(action)
      ? {
          label: action.label,
          handler: (e: ReactMouseEvent<HTMLButtonElement>) => {
            action.handler(e);
            removeToasts([toast]);
          },
        }
      : action;
  const toastProps = {
    title,
    className,
    primaryButton,
    secondaryButton,
    ...onClose,
    action: mappedAction,
  };

  if (type === "success") {
    return <SuccessToast {...toastProps}>{toast.description}</SuccessToast>;
  }

  if (type === "error") {
    return <ErrorToast {...toastProps}>{toast.description}</ErrorToast>;
  }

  if (type === "warning") {
    return <WarningToast {...toastProps}>{toast.description}</WarningToast>;
  }

  return <InfoToast {...toastProps}>{toast.description}</InfoToast>;
};

export const ToastConsumer = () => {
  const { activeToasts } = useNotifications();
  return (
    <>
      {activeToasts.map((toast) => {
        return <ToastContainer key={toast.id} toast={toast} />;
      })}
    </>
  );
};
